import { IBodyCell, IBodyRow, IColumn, PinnedStatus, onFilterChange } from './Types'
import sortBy from 'lodash/fp/sortBy'
import { KnockOperator, KnockOrderByOptions } from './Knock/KnockTypes'
import { isSpecialIndex } from './utils'
import { ISetPaginationProperty } from './Hooks/use-pagination-manager'
import React from 'react'

interface IFilterChangeValue {
  filter?: string
  filterOperator?: KnockOperator
  sortOrder?: KnockOrderByOptions
  sortIndex?: number
}

interface IGridState {
  headers: IColumn[]
  data: IBodyRow[]
  hovered: number | null
}

interface IWithState {
  grid: IGridState
  setGrid: (grid: IGridState) => void
}

export interface UseGridStateProps {
  /**
   * Called when the filter of a column got updated
   *
   * @param columns A list of all columns
   */
  onFilterChange: onFilterChange

  /**
     * Called when one of the columns switched positions with another

    * @param columns A list of all columns
     */
  onColumnMove: (columns: IColumn[]) => void

  /**
   * Called when one of the columns is resized
   *
   * @param columns A list of all columns
   */
  onColumnResize: (columns: IColumn[]) => void

  /**
   * To reset page when using filter
   *
   * @param setPaginationProperty An object with specific pagination properties
   */
  setPaginationProperty: (args: ISetPaginationProperty) => void

  headers: IColumn[]
  data: IBodyRow[]
}

const hideHiddenColumns = (columns: IColumn[]) => (columns || []).filter(h => h.visible !== false)
const sortColumnsByIndex = sortBy<IColumn>(column => column.index)
const sortDataByIndex = (columns: IColumn[]) => sortBy<IBodyCell>(cell => columns.find(col => col.key === cell.key)?.index || 0)

interface ColumnWithoutSpecialIndex extends Omit<IColumn, 'index'> {
  index: number
}

export const findLastIndexByStatus = (columns: IColumn[], pinnedStatus: PinnedStatus, defaultIndex = 0) =>
  columns
    .filter((col): col is ColumnWithoutSpecialIndex => col.pinned === pinnedStatus && !isSpecialIndex(col.index))
    .reduce((prev, col) => (col.index > prev ? col.index : prev), defaultIndex)

export const findFirstIndexByStatus = (columns: IColumn[], pinnedStatus: PinnedStatus) => {
  const filteredColumns = columns.filter(
    (col): col is ColumnWithoutSpecialIndex => col.pinned === pinnedStatus && !isSpecialIndex(col.index)
  )

  if (filteredColumns.length === 0) {
    if (pinnedStatus === PinnedStatus.Right) {
      return findLastIndexByStatus(columns, PinnedStatus.Center, 0)
    } else if (pinnedStatus === PinnedStatus.Center) {
      return findLastIndexByStatus(columns, PinnedStatus.Left, 0)
    }

    return 0
  }

  return filteredColumns.reduce((prev, col) => (col.index < prev ? col.index : prev), columns.length)
}

export const onHeaderMove =
  ({ setGrid, grid, onColumnMove }: UseGridStateProps & IWithState) =>
  (source: IColumn, target: IColumn | number, pinnedStatus?: PinnedStatus) => {
    const sourceIndex = source.index
    let targetIndex = typeof target === 'number' ? target : target.index

    if (isSpecialIndex(targetIndex)) {
      return
    }

    /**
     * PinnedStatus takes priority when it's defined.
     * Otherwise you can get pinned but have a wrong index
     */
    if (pinnedStatus) {
      targetIndex = findFirstIndexByStatus(grid.headers, pinnedStatus)
    }

    const headers = sortColumnsByIndex(
      grid.headers.map(header => {
        if (isSpecialIndex(header.index) || isSpecialIndex(targetIndex) || isSpecialIndex(sourceIndex)) {
          return header
        }

        let newIndex = header.index

        if (newIndex === targetIndex) {
          newIndex = newIndex + (targetIndex > sourceIndex ? -1 : 1)
        } else if (newIndex <= targetIndex && newIndex > sourceIndex) {
          newIndex--
        } else if (newIndex > targetIndex && newIndex < sourceIndex) {
          newIndex++
        }

        if (header.key === source.key) {
          newIndex = targetIndex
        }

        return {
          ...header,
          index: header.key === source.key ? targetIndex : newIndex,
          pinned: header.key === source.key ? pinnedStatus || (typeof target === 'object' && target.pinned) || header.pinned : header.pinned
        }
      })
    )

    const updatedGrid = {
      ...grid,
      headers,
      data: grid.data.map(row => ({
        ...row,
        cols: sortDataByIndex(headers)(row.cols)
      }))
    }

    setGrid(updatedGrid)
    onColumnMove(updatedGrid.headers)
  }

function mapGridToUpdatedColumnWidths(grid: IGridState, key: string, width: number) {
  return {
    ...grid,
    headers: grid.headers.map(header => {
      if (header.key === key && header.width + width < 100) {
        return header
      }

      return {
        ...header,
        width: header.key === key ? header.width + width : header.width
      }
    })
  }
}

export const resizeHeader =
  ({ setGrid, grid }: UseGridStateProps & IWithState) =>
  (key: string, width: number) => {
    const newGrid = mapGridToUpdatedColumnWidths(grid, key, width)

    setGrid(newGrid)
  }

const commitHeaderResize =
  ({ grid, onColumnResize }: UseGridStateProps & IWithState) =>
  (key: string, width: number) => {
    const newGrid = mapGridToUpdatedColumnWidths(grid, key, width)

    onColumnResize(newGrid.headers)
  }

export const onHover =
  ({ setGrid, grid }: UseGridStateProps & IWithState) =>
  (index: number | null) => {
    setGrid({
      ...grid,
      hovered: index
    })
  }

export const onFilterChangeFunc = (props: UseGridStateProps & IWithState) => (changedHeader: IColumn, value: IFilterChangeValue) => {
  const { setGrid, grid, onFilterChange, setPaginationProperty } = props
  if (value.sortOrder && value.sortOrder !== changedHeader.sortOrder && changedHeader.sortOrder === '') {
    const lastIndex = grid.headers.reduce((prev, header) => {
      if (!header.sortIndex) {
        return prev
      }

      return header.sortIndex > prev ? header.sortIndex : prev
    }, 0)

    if (lastIndex === 9) {
      return
    } else {
      value.sortIndex = lastIndex + 1
    }
  } else if (value.sortOrder === '') {
    value.sortIndex = 0
  }

  const updatedGrid = {
    ...grid,
    headers: grid.headers.map(header => {
      if (header.key === changedHeader.key) {
        return {
          ...header,
          ...value
        }
      }

      if (value.sortOrder === '' && header.sortIndex && changedHeader.sortIndex && header.sortIndex > changedHeader.sortIndex) {
        return {
          ...header,
          sortIndex: header.sortIndex - 1
        }
      }

      return header
    })
  }
  setPaginationProperty({ Page: 1 })
  setGrid(updatedGrid)
  onFilterChange(updatedGrid.headers)

  return updatedGrid
}

const useGridState = (props: UseGridStateProps) => {
  const { headers, data } = props

  const [grid, setGrid] = React.useState<IGridState>({
    headers: sortColumnsByIndex(hideHiddenColumns(headers)),
    data,
    hovered: null
  })

  React.useEffect(() => {
    const updatedGrid = {
      ...grid,
      data: data.map(row => ({
        ...row,
        cols: sortDataByIndex(grid.headers)(row.cols)
      }))
    }

    updatedGrid.headers = grid.headers.map(header => {
      const nextHeader = headers.find(h => h.key === header.key)

      if (!nextHeader) {
        return header
      }

      return {
        ...header,
        filter: nextHeader.filter,
        sortOrder: nextHeader.sortOrder,
        sortIndex: nextHeader.sortIndex
      }
    })

    setGrid(updatedGrid)
  }, [data, headers])

  const initProps = { ...props, grid, setGrid }

  return {
    grid,
    updateIndex: onHeaderMove(initProps),
    commitHeaderResize: commitHeaderResize(initProps),
    resizeHeader: resizeHeader(initProps),
    onHover: onHover(initProps),
    onFilterChange: onFilterChangeFunc(initProps)
  }
}

export default useGridState
